import React from 'react'

import gql from 'graphql-tag'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { MAX_FILE_SIZE } from '~/common/constants'
import { useRouter } from 'next/router'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import {
  useMaxAttachmentSizeProviderProjectQuery,
  useMaxAttachmentSizeProviderTaskQuery,
  useMaxAttachmentSizeProviderTemplateQuery,
} from '~/common/types/graphql'
import { Subtract } from 'utility-types'

const MaxAttachmentSize = React.createContext<number | null | undefined>(null)

const useMaxAttachmentSize = () => {
  const maxFileSize = React.useContext<number | null | undefined>(
    MaxAttachmentSize,
  )

  if (!maxFileSize) {
    return MAX_FILE_SIZE
  }

  return maxFileSize * 1000000
}

const MaxAttachmentSizeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [maxAttachmentSize, setMaxAttachmentSize] = React.useState<
    number | undefined | null
  >(null)

  const { currentUser } = useCurrentUserContext()
  const router = useRouter()
  const projectId = router.query['project-id'] || router.query['projectId']
  const templateId = router.query['id']
  const taskId = router.query['task-id']
  const isAddingTemplate = router.query['add-template']
  const isAddingProject = router.query['add-project']

  const { data: projectResult, loading: loadingProject } =
    useMaxAttachmentSizeProviderProjectQuery({
      variables: {
        projectId: typeof projectId === 'string' ? projectId : '',
      },
      skip: !projectId || !currentUser,
    })

  const { data: taskResult, loading: loadingTask } =
    useMaxAttachmentSizeProviderTaskQuery({
      variables: {
        id: typeof taskId === 'string' ? taskId : '',
      },
      skip: !taskId || !!projectId || !!templateId,
    })

  const { data: templateResult, loading: loadingTemplate } =
    useMaxAttachmentSizeProviderTemplateQuery({
      variables: {
        templateId: typeof templateId === 'string' ? templateId : '',
      },
      skip: !templateId,
    })

  React.useEffect(() => {
    if (projectResult || taskResult || templateResult) {
      setMaxAttachmentSize(
        projectResult?.project?.organization?.adminSettings.uploadLimitMb ||
          taskResult?.task?.template?.organization?.adminSettings
            .uploadLimitMb ||
          templateResult?.blueprintTemplate?.organization?.adminSettings
            .uploadLimitMb,
      )
    }
  }, [loadingTemplate, loadingTask, loadingProject])

  React.useEffect(() => {
    if (isAddingProject === 'true' || isAddingTemplate === 'true') {
      setMaxAttachmentSize(
        currentUser?.organization?.adminSettings.uploadLimitMb,
      )
    }
  }, [isAddingProject, isAddingTemplate])

  return (
    <MaxAttachmentSize.Provider value={maxAttachmentSize}>
      {children}
    </MaxAttachmentSize.Provider>
  )
}

export type WithMaxAttachmentSizeContextProps<T = {}> = T & {
  maxFileSize: number
}

const withMaxAttachmentSizeContext = <
  P extends WithMaxAttachmentSizeContextProps,
>(
  Component: React.ComponentType<P>,
) => {
  const ComponentContext = React.forwardRef<
    any,
    Subtract<P, WithMaxAttachmentSizeContextProps>
  >((props, ref) => {
    const maxFileSize = useMaxAttachmentSize()

    return (
      <Component
        {...(props as unknown as P)}
        ref={ref}
        maxFileSize={maxFileSize}
      />
    )
  })

  return hoistNonReactStatics(ComponentContext, Component)
}

gql`
  query MaxAttachmentSizeProvider_Task($id: ID!) {
    task(id: $id) {
      id
      template {
        organization {
          adminSettings {
            uploadLimitMb
          }
        }
      }
      __typename
    }
  }
`
gql`
  query MaxAttachmentSizeProvider_Template($templateId: ID!) {
    blueprintTemplate(id: $templateId) {
      id
      organization {
        adminSettings {
          uploadLimitMb
        }
      }
      __typename
    }
  }
`

gql`
  query MaxAttachmentSizeProvider_Project($projectId: ID!) {
    project(id: $projectId) {
      id
      organization {
        adminSettings {
          uploadLimitMb
        }
      }
      __typename
    }
  }
`

export {
  withMaxAttachmentSizeContext,
  MaxAttachmentSizeProvider,
  useMaxAttachmentSize,
}
